/* Contextual, conservative recommendations. No personal history or tracking identifiers. */
(function (root) {
  'use strict';
  const norm = value => String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const id = value => String(value || '').split('/').pop();
  function kind(p) {
    const s = norm(`${p.type} ${p.title}`);
    if (/sytrad|trad polyester/.test(s)) return 'thread';
    if (/virknal|crochet/.test(s)) return 'hook';
    if (/stickor|sticka|crasytrio/.test(s)) return 'needle';
    if (/tyg|vav|fleece/.test(s)) return 'fabric';
    if (/garn/.test(norm(p.type))) return 'yarn';
    if (/sax|scissor/.test(s)) return 'scissors';
    if (/ullnal|stoppnal|tapisseri|ihopsyning/.test(s)) return 'darning';
    if (/markor/.test(s)) return 'markers';
    if (/mattband|rullmatt/.test(s)) return 'measure';
    if (/somspretta|somspratta/.test(s)) return 'ripper';
    if (/knappnal|synal/.test(s) && !/packnal|hantverksnal/.test(norm(p.title))) return 'sewing';
    return 'other';
  }
  function sizes(value) {
    const s = norm(value).replace(/,/g, '.');
    const range = s.match(/(\d+(?:\.\d+)?)\s*[–—-]\s*(\d+(?:\.\d+)?)\s*(?:mm)?$/);
    if (range) {
      const min = Number(range[1]), max = Number(range[2]);
      return min > 0 && max <= 30 && max >= min ? [min, max] : [];
    }
    const single = s.match(/(?:^|\s|;)\s*(\d+(?:\.\d+)?)\s*mm\b/);
    return single ? [Number(single[1]), Number(single[1])] : [];
  }
  function inRange(variant, range) {
    const size = sizes(variant.title);
    return size.length && range.length && size[0] >= range[0] && size[0] <= range[1];
  }
  function cartLines(cart) {
    return (cart.items || []).flatMap(line => [line, ...(line.item_components || [])]);
  }
  function score(catalog, cart, {seed = 0, previous = [], inspiration = false} = {}) {
    const lines = cartLines(cart);
    if (!lines.length) return [];
    const ids = new Set(lines.map(l => id(l.product_id)));
    const handles = new Set(lines.map(l => l.handle).filter(Boolean));
    const children = new Set(catalog.flatMap(p => p.children || []));
    const sources = catalog.filter(p => ids.has(id(p.id)) || handles.has(p.handle) || (p.children || []).some(h => handles.has(h)));
    const types = new Set(sources.map(kind));
    const has = k => types.has(k);
    const existingTools = lines.map(l => ({...catalog.find(p => id(p.id) === id(l.product_id)), title: l.variant_title || l.title}));
    return catalog.flatMap((p, rank) => {
      if (p.hidden || children.has(p.handle) || ids.has(id(p.id)) || handles.has(p.handle) || (p.children || []).some(h => handles.has(h))) return [];
      const category = kind(p);
      let relevance = 0, reason = '', allowedIds = [], ranges = [];
      if (inspiration) {
        // Inspiration is not a compatibility claim. Keep tools out of this pool:
        // a tool that failed sizing checks must never return as a popular tip.
        if (!['yarn', 'fabric'].includes(category) || !p.image) return [];
        const knitting = ['yarn', 'needle', 'hook', 'darning', 'markers'].some(has);
        const sewing = ['fabric', 'thread', 'sewing', 'ripper'].some(has);
        if (knitting && !sewing && category !== 'yarn' || sewing && !knitting && category !== 'fabric') return [];
        relevance = 30;
        reason = category === 'yarn' ? 'Ett garntips till nästa projekt.' : 'Ett tygtips till nästa projekt.';
      } else if (category === 'needle' || category === 'hook') {
        const covered = size => existingTools.some(tool => kind({...tool, title: catalog.find(x => id(x.id) === id(tool.id))?.title || tool.type}) === category && inRange(tool, size));
        for (const yarn of sources.filter(s => kind(s) === 'yarn')) {
          for (const m of yarn.matches || []) {
            if (m.handle !== p.handle && id(m.productId) !== id(p.id)) continue;
            for (const v of m.variants || []) {
              const range = sizes(v.size || v.title);
              if (range.length && !covered(range)) {
                if (v.id) allowedIds.push(id(v.id));
                ranges.push(range);
              }
            }
          }
          const range = sizes(category === 'needle' ? yarn.needle : yarn.hook);
          if (range.length && !covered(range)) ranges.push(range);
        }
        if (ranges.length || allowedIds.length) {
          relevance = 100 + (/addi/i.test(p.vendor + p.title) ? 5 : 0);
          reason = category === 'needle' ? 'Storlek som passar garnet. Välj längd efter ditt mönster.' : 'Virknålsstorlek som passar garnet.';
        }
      } else if (category === 'thread' && has('fabric') && !has('thread') && !/extra stark|knapp|top.?stitch/i.test(p.title)) {
        relevance = 90; reason = 'Sytråd till ditt tygprojekt – välj en passande färg.';
      } else if (category === 'scissors' && (has('fabric') || has('yarn')) && !has('scissors')) {
        const small = /broderi|garn|liten/i.test(p.title);
        const fabricSafe = /sharpist|tyg|skr[aä]ddar|dressmaking/i.test(p.title);
        if (has('fabric') && fabricSafe || has('yarn') && small) {
          relevance = 80; reason = has('fabric') && fabricSafe ? 'En sax till ditt tygprojekt.' : 'En liten sax för garnändar.';
        }
      } else if (['darning', 'markers'].includes(category) && has('yarn') && !has(category)) {
        relevance = 70; reason = category === 'darning' ? 'För att fästa garnändar och sy ihop arbetet.' : 'Håll reda på maskor och varv.';
      } else if (['ripper', 'sewing'].includes(category) && has('fabric') && !has(category)) {
        relevance = 70; reason = 'Ett praktiskt tillbehör till sömnaden.';
      } else if (category === 'measure' && (has('yarn') || has('fabric')) && !has(category)) {
        relevance = 65; reason = 'Kontrollera måtten under arbetets gång.';
      }
      if (!relevance) return [];
      let hash = Number(seed) || 0;
      for (const c of String(p.id)) hash = Math.imul(hash ^ c.charCodeAt(0), 16777619);
      const rotation = (hash >>> 0) % (inspiration ? 7 : 17);
      // Sales of combined families occur on child products, not the visible parent.
      const familyRanks = (p.children || []).map(h => catalog.findIndex(x => x.handle === h)).filter(r => r >= 0);
      const popularity = 8 * (1 - Math.min(rank, ...familyRanks) / Math.max(catalog.length, 1));
      return [{...p, category, group: inspiration ? 'inspiration' : 'accessory', reason, allowedIds, ranges,
        score: relevance + popularity * (inspiration ? 4 : 1) + rotation - (previous.includes(p.handle) ? (inspiration ? 6 : 18) : 0)}];
    }).sort((a, b) => b.score - a.score);
  }
  function eligible(candidate, detail) {
    const variants = (detail.variants || []).filter(v => v.stock === true &&
      (!['needle', 'hook'].includes(candidate.category) || candidate.allowedIds.includes(id(v.id)) || candidate.ranges.some(r => inRange(v, r))));
    return variants.length ? {...candidate, ...detail, variants, variantCount: (detail.variants || []).length} : null;
  }
  function diverse(candidates, limit, {fill = true} = {}) {
    if (limit <= 0) return [];
    const counts = {}, out = [];
    for (const p of candidates) {
      if ((counts[p.category] || 0) >= 2) continue;
      counts[p.category] = (counts[p.category] || 0) + 1; out.push(p);
      if (out.length >= limit) break;
    }
    // Prefer different categories first, but don't leave empty places when more
    // relevant, stock-checked products are available in a represented category.
    for (const p of fill ? candidates : []) {
      if (out.length >= limit) break;
      if (!out.includes(p)) out.push(p);
    }
    return out;
  }
  function select(candidates, limit) {
    const unique = [], seen = new Set();
    for (const p of candidates) {
      const key = p.handle || id(p.id);
      if (seen.has(key)) continue;
      seen.add(key); unique.push(p);
    }
    const accessories = diverse(unique.filter(p => p.group !== 'inspiration'), limit);
    return accessories.concat(unique.filter(p => p.group === 'inspiration').slice(0, Math.max(0, limit - accessories.length)));
  }
  const inspiration = (catalog, cart, options = {}) => score(catalog, cart, {...options, inspiration: true});
  const api = {kind, sizes, score, eligible, diverse, select, inspiration, cartLines};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.KickisUpsellEngine = api;
})(typeof window !== 'undefined' ? window : globalThis);
